import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";

/* ===== helpers ogólne ===== */

function getRandomFromArray(arr) {
  return Math.floor(Math.random() * arr.length) >= 0
    ? arr[Math.floor(Math.random() * arr.length)]
    : arr[0];
}

function getRandomInt(min, max, exclude = []) {
  const values = [];
  for (let i = min; i <= max; i++) {
    if (!exclude.includes(i)) values.push(i);
  }
  return getRandomFromArray(values);
}

// zapis liniowy kx + m
function formatLinearTerm(k, m) {
  const parts = [];
  if (k === 1) {
    parts.push("x");
  } else if (k === -1) {
    parts.push("-x");
  } else {
    parts.push(`${k}x`);
  }

  if (m !== 0) {
    const sign = m > 0 ? "+" : "-";
    parts.push(`${sign} ${Math.abs(m)}`);
  }

  return parts.join(" ");
}

/* ===== helpers do ciągu geometrycznego ===== */

// sprawdzamy, czy (A,B,C) tworzy rzeczywiście ciąg geometryczny
function isGeometricTriplet(A, B, C) {
  // przypadek (0,0,0) – przyjmujemy jako ciąg geometryczny
  if (A === 0 && B === 0 && C === 0) return true;

  // jeśli A lub B = 0, a nie mamy (0,0,0), to nie jest ciąg geometryczny
  if (A === 0 || B === 0) return false;

  // klasyczny warunek q jest stałe
  return B * B === A * C;
}

// odrzucamy sytuacje, w których dwa wyrazy są identyczne jako funkcje x
function hasEqualTerms(p1, aVal, p2, bVal, p3, cVal) {
  const t12 = p1 === p2 && aVal === bVal;
  const t13 = p1 === p3 && aVal === cVal;
  const t23 = p2 === p3 && bVal === cVal;
  return t12 || t13 || t23;
}

const labels = {
  "0": "brak rozwiązań",
  "1": "jedno rozwiązanie",
  "2": "dwa rozwiązania"
};

const within = (x, y) => Math.abs(x - y) < 0.01;

// proste formatowanie całkowitej liczby do LaTeX
function formatIntLatex(value) {
  return `${value}`;
}

/* ===== GENERATOR ===== */
/*
  mode:
  - "quad"      -> równanie kwadratowe z Δ > 0 (0/1/2 prawdziwych rozwiązań po odrzuceniu paraciągów)
  - "deltaNeg"  -> równanie kwadratowe z Δ < 0 (z automatu brak rozwiązań)
  - "linear"    -> kwadraty się skracają, zostaje równanie liniowe (tutaj wymuszamy 1 prawdziwe rozwiązanie)
*/

export function generateTask() {
  while (true) {
    const r = Math.random();
    const mode = r < 0.2 ? "deltaNeg" : r < 0.4 ? "linear" : "quad";

    if (mode === "quad") {
      // klasyczny przypadek: Δ > 0 i dwie całkowite rzeczywiste wartości x
      while (true) {
        const p1 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);
        const p2 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);
        const p3 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);

        const aVal = getRandomInt(-7, 7);
        const bVal = getRandomInt(-7, 7);
        const cVal = getRandomInt(-7, 7);

        // odrzucamy przypadki, gdy jakieś dwa wyrazy są identyczne
        if (hasEqualTerms(p1, aVal, p2, bVal, p3, cVal)) continue;

        const A = (x) => p1 * x + aVal;
        const B = (x) => p2 * x + bVal;
        const C = (x) => p3 * x + cVal;

        const A_coef = p2 * p2 - p1 * p3;
        if (A_coef === 0) continue; // tu nie chcemy liniowego

        const B_coef = 2 * p2 * bVal - (p1 * cVal + p3 * aVal);
        const C_coef = bVal * bVal - aVal * cVal;

        const delta = B_coef * B_coef - 4 * A_coef * C_coef;
        if (delta <= 0) continue;

        const sqrtDelta = Math.sqrt(delta);
        if (!Number.isInteger(sqrtDelta)) continue;

        const x1 = (-B_coef + sqrtDelta) / (2 * A_coef);
        const x2 = (-B_coef - sqrtDelta) / (2 * A_coef);
        if (!Number.isInteger(x1) || !Number.isInteger(x2) || x1 === x2)
          continue;

        const solutionsRaw = [x1, x2].sort((a, b) => a - b);
        const validSolutions = solutionsRaw.filter((x) =>
          isGeometricTriplet(A(x), B(x), C(x))
        );

        const numSolutions = String(validSolutions.length); // '0','1','2'

        const termA = formatLinearTerm(p1, aVal);
        const termB = formatLinearTerm(p2, bVal);
        const termC = formatLinearTerm(p3, cVal);

        return {
          type: "FindXForGeometricTripletTask",
          data: {
            mode,
            a: termA,
            b: termB,
            c: termC,
            p1,
            p2,
            p3,
            aVal,
            bVal,
            cVal,
            A_coef,
            B_coef,
            C_coef,
            delta,
            sqrtDelta,
            solutionsRaw,
            validSolutions,
            numSolutions
          }
        };
      }
    }

    if (mode === "deltaNeg") {
      // chcemy Δ < 0, wtedy z automatu brak rozwiązań
      while (true) {
        const p1 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);
        const p2 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);
        const p3 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);

        const aVal = getRandomInt(-7, 7);
        const bVal = getRandomInt(-7, 7);
        const cVal = getRandomInt(-7, 7);

        // odrzucamy identyczne wyrazy
        if (hasEqualTerms(p1, aVal, p2, bVal, p3, cVal)) continue;

        const A_coef = p2 * p2 - p1 * p3;
        if (A_coef === 0) continue; // nie chcemy liniowego tutaj

        const B_coef = 2 * p2 * bVal - (p1 * cVal + p3 * aVal);
        const C_coef = bVal * bVal - aVal * cVal;

        const delta = B_coef * B_coef - 4 * A_coef * C_coef;
        if (delta >= 0) continue; // wymuszamy < 0

        const termA = formatLinearTerm(p1, aVal);
        const termB = formatLinearTerm(p2, bVal);
        const termC = formatLinearTerm(p3, cVal);

        return {
          type: "FindXForGeometricTripletTask",
          data: {
            mode,
            a: termA,
            b: termB,
            c: termC,
            p1,
            p2,
            p3,
            aVal,
            bVal,
            cVal,
            A_coef,
            B_coef,
            C_coef,
            delta,
            sqrtDelta: null,
            solutionsRaw: [],
            validSolutions: [],
            numSolutions: "0"
          }
        };
      }
    }

    if (mode === "linear") {
      // chcemy A_coef = 0 (kwadraty się skracają) oraz jedno "prawdziwe" rozwiązanie
      while (true) {
        const p2 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);
        const p3 = getRandomFromArray([1, 2, 3, 4, 5, 6, 7]);

        const p1Candidate = (p2 * p2) / p3;
        if (!Number.isInteger(p1Candidate) || p1Candidate < 1 || p1Candidate > 7)
          continue;
        const p1 = p1Candidate;

        const aVal = getRandomInt(-7, 7);
        const bVal = getRandomInt(-7, 7);
        const cVal = getRandomInt(-7, 7);

        // odrzucamy identyczne wyrazy
        if (hasEqualTerms(p1, aVal, p2, bVal, p3, cVal)) continue;

        const A = (x) => p1 * x + aVal;
        const B = (x) => p2 * x + bVal;
        const C = (x) => p3 * x + cVal;

        const A_coef = p2 * p2 - p1 * p3; // powinno wyjść 0
        if (A_coef !== 0) continue;

        const B_coef = 2 * p2 * bVal - (p1 * cVal + p3 * aVal);
        const C_coef = bVal * bVal - aVal * cVal;
        if (B_coef === 0) continue;

        const x0 = -C_coef / B_coef;
        if (!Number.isInteger(x0)) continue;

        if (!isGeometricTriplet(A(x0), B(x0), C(x0))) continue;

        const termA = formatLinearTerm(p1, aVal);
        const termB = formatLinearTerm(p2, bVal);
        const termC = formatLinearTerm(p3, cVal);

        return {
          type: "FindXForGeometricTripletTask",
          data: {
            mode,
            a: termA,
            b: termB,
            c: termC,
            p1,
            p2,
            p3,
            aVal,
            bVal,
            cVal,
            A_coef,
            B_coef,
            C_coef,
            delta: null,
            sqrtDelta: null,
            solutionsRaw: [x0],
            validSolutions: [x0],
            numSolutions: "1"
          }
        };
      }
    }
  }
}

/* ===== TREŚĆ ===== */

export function renderTask(task) {
  const { a, b, c } = task.data;

  return (
    <>
      <p>
        Dana jest trójka liczb:{" "}
        <InlineMath math={`(${a},\\ ${b},\\ ${c})`} />.
      </p>
      <p>
        Oblicz wszystkie wartości <InlineMath math={"x"} />, dla których ta
        trójka w podanej kolejności tworzy ciąg geometryczny.
      </p>
    </>
  );
}

/* ===== INPUT (Opcja 1 – jak w QuadraticCountAndSolveTask) ===== */

export function renderInput(_, userInput = {}, onChange, disabled) {
  const count = userInput.count ?? "";

  return (
    <div className="flex flex-col gap-2">
      <SelectInput
        options={[
          { value: "0", label: "brak rozwiązań" },
          { value: "1", label: "jedno rozwiązanie" },
          { value: "2", label: "dwa rozwiązania" }
        ]}
        value={count}
        onChange={(e) =>
          onChange({ ...userInput, count: e?.target?.value ?? e })
        }
        placeholder="Wybierz liczbę rozwiązań"
        disabled={disabled}
      />

      {(count === "1" || count === "2") && (
        <div className="flex items-center gap-2">
          <span>
            <InlineMath math={"x_1 ="} />
          </span>
          <FractionInput
            value={userInput.x1 || ""}
            onChange={(val) => onChange({ ...userInput, x1: val })}
            disabled={disabled}
            label={count === "1" ? "x" : "x₁"}
          />
        </div>
      )}

      {count === "2" && (
        <div className="flex items-center gap-2">
          <span>
            <InlineMath math={"x_2 ="} />
          </span>
          <FractionInput
            value={userInput.x2 || ""}
            onChange={(val) => onChange({ ...userInput, x2: val })}
            disabled={disabled}
            label="x₂"
          />
        </div>
      )}
    </div>
  );
}

/* ===== SPRAWDZANIE ===== */

export function checkAnswer(task, userInput = {}) {
  const picked = userInput.count;
  const { numSolutions, validSolutions } = task.data;

  if (picked !== numSolutions) {
    return { correct: false, expected: [numSolutions, ...validSolutions] };
  }

  if (numSolutions === "0") {
    return { correct: true, expected: [numSolutions] };
  }

  const u1 = userInput.x1?.parsed;
  if (u1 == null || isNaN(u1)) {
    return { correct: false, expected: [numSolutions, ...validSolutions] };
  }

  if (numSolutions === "1") {
    const ok = within(u1, validSolutions[0]);
    return { correct: ok, expected: [numSolutions, ...validSolutions] };
  }

  const u2 = userInput.x2?.parsed;
  if (u2 == null || isNaN(u2)) {
    return { correct: false, expected: [numSolutions, ...validSolutions] };
  }

  const [s1, s2] = validSolutions;

  const ok =
    (within(u1, s1) && within(u2, s2)) ||
    (within(u1, s2) && within(u2, s1));

  return { correct: ok, expected: [numSolutions, ...validSolutions] };
}

/* ===== POPRAWNA ODPOWIEDŹ (krótka) ===== */

export function renderAnswer(task) {
  const { numSolutions, validSolutions } = task.data;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div>
        <strong>Liczba rozwiązań:</strong> {labels[numSolutions]}
      </div>

      {numSolutions === "1" && (
        <div style={{ marginTop: "0.5rem" }}>
          <InlineMath math={`x = ${formatIntLatex(validSolutions[0])}`} />
        </div>
      )}

      {numSolutions === "2" && (
        <>
          <div style={{ marginTop: "0.5rem" }}>
            <InlineMath
              math={`x_1 = ${formatIntLatex(validSolutions[0])}`}
            />
          </div>
          <div style={{ marginTop: "0.5rem" }}>
            <InlineMath
              math={`x_2 = ${formatIntLatex(validSolutions[1])}`}
            />
          </div>
        </>
      )}
    </div>
  );
}

/* ===== TWOJA ODPOWIEDŹ ===== */

export function renderUserAnswer(userInput = {}) {
  const count = userInput.count;
  if (!count) return <i>(brak odpowiedzi)</i>;

  const parts = [
    <div key="c">
      <strong>Liczba rozwiązań:</strong> {labels[count]}
    </div>
  ];

  if (count === "1" || count === "2") {
    const x1 =
      userInput.x1?.raw && formatLatexPreview(userInput.x1.raw, "x_1");
    parts.push(
      <div key="x1" style={{ marginTop: "0.5rem" }}>
        {x1 ? <InlineMath math={x1} /> : <i>(brak x₁)</i>}
      </div>
    );
  }

  if (count === "2") {
    const x2 =
      userInput.x2?.raw && formatLatexPreview(userInput.x2.raw, "x_2");
    parts.push(
      <div key="x2" style={{ marginTop: "0.5rem" }}>
        {x2 ? <InlineMath math={x2} /> : <i>(brak x₂)</i>}
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>{parts}</div>
  );
}

/* ===== WYJAŚNIENIE ===== */

export function renderExplanation(task) {
  const {
    mode,
    a,
    b,
    c,
    p1,
    p2,
    p3,
    aVal,
    bVal,
    cVal,
    A_coef,
    B_coef,
    C_coef,
    delta,
    sqrtDelta,
    solutionsRaw,
    validSolutions,
    numSolutions
  } = task.data;

  const signWithSpace = (x) => (x >= 0 ? `+ ${x}` : `- ${Math.abs(x)}`);

  const coefTerm = (coef, variable) => {
    if (coef === 0) return "";
    const sign = coef > 0 ? "+ " : "- ";
    const abs = Math.abs(coef);
    const coeffPart = abs === 1 && variable ? "" : abs;
    return `${sign}${coeffPart}${variable}`;
  };

  const quadPolyStr = (A, B, C) => {
    return `${A}x^2${coefTerm(B, "x")}${coefTerm(C, "")}`;
  };

  const quadraticStr =
    `${A_coef}x^2` +
    `${coefTerm(B_coef, "x")}` +
    `${coefTerm(C_coef, "")}` +
    " = 0";

  const A = (x) => p1 * x + aVal;
  const B = (x) => p2 * x + bVal;
  const C = (x) => p3 * x + cVal;

  // współczynniki przy rozpisaniu lewej i prawej strony
  const leftA = p2 * p2;
  const leftB = 2 * p2 * bVal;
  const leftC = bVal * bVal;

  const rightA = p1 * p3;
  const rightB = p1 * cVal + p3 * aVal;
  const rightC = aVal * cVal;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Dla trzech kolejnych wyrazów ciągu
        geometrycznego <InlineMath math="(a,b,c)" /> zachodzi warunek:
      </div>
      <BlockMath math={"b^2 = a c"} />

      <div>
        <strong>Krok 2:</strong> W naszym zadaniu mamy trójkę{" "}
        <InlineMath math={`(${a},\\ ${b},\\ ${c})`} />, więc podstawiamy:
      </div>
      <BlockMath
        math={`(${p2}x ${signWithSpace(bVal)})^2 = (${p1}x ${signWithSpace(
          aVal
        )})(${p3}x ${signWithSpace(cVal)})`}
      />
      <BlockMath
        math={`${quadPolyStr(leftA, leftB, leftC)} = ${quadPolyStr(
          rightA,
          rightB,
          rightC
        )}`}
      />

      <div>
        <strong>Krok 3:</strong> Po uporządkowaniu otrzymujemy równanie
        w zmiennej <InlineMath math="x" />:
      </div>

      {mode === "linear" ? (
        <BlockMath
          math={`${coefTerm(B_coef, "x").trim()}${coefTerm(
            C_coef,
            ""
          )} = 0`}
        />
      ) : (
        <BlockMath math={quadraticStr} />
      )}

      {mode === "deltaNeg" && (
        <>
          <div>
            <strong>Krok 4:</strong> Liczymy deltę równania kwadratowego:
          </div>
          <BlockMath math={`\\Delta = ${B_coef}^2 - 4 \\cdot ${A_coef} \\cdot ${C_coef} = ${delta}`} />
          <div>
            Ponieważ <InlineMath math={"\\Delta &lt; 0"} /> – równanie nie ma
            rozwiązań rzeczywistych. Nie istnieje więc żadne{" "}
            <InlineMath math="x" />, dla którego nasza trójka tworzy ciąg
            geometryczny.
          </div>
          <div>
            <strong>Odpowiedź:</strong>{" "}
            <BlockMath math={"\\text{brak rozwiązań}"} />
          </div>
        </>
      )}

      {mode === "linear" && (
        <>
          <div>
            <strong>Krok 4:</strong> Mamy równanie liniowe, więc nie liczymy
            delty, tylko rozwiązujemy:
          </div>
          <BlockMath
            math={`${
              coefTerm(B_coef, "x").trim() + coefTerm(C_coef, "")
            } = 0`}
          />
          <BlockMath
            math={`x = -\\dfrac{${C_coef}}{${B_coef}} = ${solutionsRaw[0]}`}
          />

          <div>
            <strong>Krok 5:</strong> Sprawdzamy, że dla tej wartości trójka
            rzeczywiście jest ciągiem geometrycznym:
          </div>
          {(() => {
            const x = solutionsRaw[0];
            const Aval = A(x);
            const Bval = B(x);
            const Cval = C(x);
            return (
              <>
                <BlockMath
                  math={`\\text{Dla } x = ${x}: \\quad (${Aval},\\ ${Bval},\\ ${Cval})`}
                />
                <BlockMath
                  math={`${Bval}^2 = ${Bval * Bval}, \\quad ${Aval} \\cdot ${Cval} = ${
                    Aval * Cval
                  }`}
                />
                <InlineMath math={"\\Rightarrow\\ tworzy\\ ciąg\\ geometryczny"} />
              </>
            );
          })()}

          <div>
            <strong>Odpowiedź:</strong>{" "}
            <BlockMath
              math={`x = ${formatIntLatex(validSolutions[0])}`}
            />
          </div>
        </>
      )}

      {mode === "quad" && (
        <>
          <div>
            <strong>Krok 4:</strong> Liczymy deltę:
          </div>
          <BlockMath math={`\\Delta = ${B_coef}^2 - 4 \\cdot ${A_coef} \\cdot ${C_coef} = ${delta}`} />
          <BlockMath math={`\\sqrt{\\Delta} = ${sqrtDelta}`} />

          <div>
            <strong>Krok 5:</strong> Korzystamy ze wzoru kwadratowego:
          </div>
          <BlockMath
            math={`x = \\dfrac{-(${B_coef}) \\pm ${sqrtDelta}}{2 \\cdot ${A_coef}}`}
          />
          <BlockMath
            math={`x_1 = ${solutionsRaw[0]},\\quad x_2 = ${solutionsRaw[1]}`}
          />

          <div>
            <strong>Krok 6:</strong> Sprawdzamy, że dla tych wartości trójka
            rzeczywiście jest ciągiem geometrycznym:
          </div>

          {solutionsRaw.map((x, i) => {
            const Aval = A(x);
            const Bval = B(x);
            const Cval = C(x);
            const geo = isGeometricTriplet(Aval, Bval, Cval);

            return (
              <div key={i} className="mb-2">
                <BlockMath
                  math={`\\text{Dla } x = ${x}: \\quad (${Aval},\\ ${Bval},\\ ${Cval})`}
                />
                <BlockMath
                  math={`${Bval}^2 = ${Bval * Bval}, \\quad ${Aval} \\cdot ${Cval} = ${
                    Aval * Cval
                  }`}
                />
                <div>
                  {geo ? (
                    <InlineMath math={"\\Rightarrow\\ tworzy\\ ciąg\\ geometryczny"} />
                  ) : (
                    <InlineMath math={"\\Rightarrow\\ nie\\ jest\\ ciągiem\\ geometrycznym"} />
                  )}
                </div>
              </div>
            );
          })}

          <div>
            <strong>Odpowiedź:</strong>
          </div>

          {numSolutions === "0" && (
            <BlockMath math={"\\text{brak rozwiązań}"} />
          )}

          {numSolutions === "1" && (
            <BlockMath
              math={`x = ${formatIntLatex(validSolutions[0])}`}
            />
          )}

          {numSolutions === "2" && (
            <BlockMath
              math={`x_1 = ${formatIntLatex(
                validSolutions[0]
              )},\\quad x_2 = ${formatIntLatex(validSolutions[1])}`}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ===== EXPORT ===== */

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer
};
